import React, { forwardRef, useContext, useState } from 'react'

import { TiptapCollabProvider } from '@tiptap-pro/provider'
import { Editor, EditorContent } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import {
  TbBold,
  TbItalic,
  TbList,
  TbListNumbers,
  TbStrikethrough,
} from 'react-icons/tb'
import { twMerge } from 'tailwind-merge'
import * as Y from 'yjs'

import {
  AttachmentMenu,
  Attachments,
} from '@/components/common/TextArea/RichTextArea/AttachmentMenu'
import { CancelButton } from '@/components/common/TextArea/RichTextArea/CancelButton'
import {
  RICH_TEXT_AREA_NO_BLUR_CLASSNAME,
  RICH_TEXT_AREA_OVERRIDE_FOCUS_CLASSNAME,
} from '@/components/common/TextArea/RichTextArea/constants'
import {
  RichTextAreaContext,
  RichTextAreaContextProvider,
  RichTextAreaStoreContextProvider,
  useRichTextAreaContext,
} from '@/components/common/TextArea/RichTextArea/context'
import { EditorButton } from '@/components/common/TextArea/RichTextArea/EditorButton'
import {
  convertToJsonContent,
  getHtmlFromJsonContent,
  isValidJsonContent,
  renderAsComponent,
  useCollaborativeRichTextArea,
  useRichTextArea,
} from '@/components/common/TextArea/RichTextArea/hooks'
import { getPlainTextFromJsonContent } from '@/components/common/TextArea/RichTextArea/lib'
import {
  CommentApprovalMenu as BaseCommentApprovalMenu,
  LinkMenu as BaseLinkMenu,
  TextMenu as BaseTextMenu,
} from '@/components/common/TextArea/RichTextArea/menus'
import { SaveButton } from '@/components/common/TextArea/RichTextArea/SaveButton'
import { ShiftEnterMessage } from '@/components/common/TextArea/RichTextArea/ShiftEnterMessage'
import { SubmitButton } from '@/components/common/TextArea/RichTextArea/SubmitButton'
import { EditorToolbar } from '@/components/common/TextArea/RichTextArea/Toolbar'
import type {
  AddCommentRequest,
  CollaborativeRichTextAreaProps,
  LocalRichTextAreaProps,
  RichTextAreaProps,
  RichTextAreaRef,
} from '@/components/common/TextArea/RichTextArea/types'
import { VersionDialog } from '@/components/common/TextArea/RichTextArea/VersionDialog'

import { PROSE_CLASSNAMES } from '@/constants/styles'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useTiptapToken } from '@/hooks/tiptap/useTiptapToken'

import type { UUID } from '@/types/Id'

interface RichTextAreaContent {
  className?: string
  containerClassName?: string
  compact?: boolean
}

interface RichTextAreaBubbleMenu {
  className?: string
}

export interface RichTextAreaHeader {
  className?: string
  buttonFocusClassName?: ({
    isActive,
    isFocused,
  }: {
    isActive: boolean
    isFocused: boolean
  }) => string
}

interface RichTextAreaFooter {
  className?: string
  children?: React.ReactNode
}

interface RichTextAreaCommentApprovalMenu {
  onApproveComments?: (ids: UUID[]) => void
}

interface RichTextAreaTextMenu {
  onAddComment?: (request: AddCommentRequest) => void
}

export const HeaderDivider: React.FC = () => {
  return <div className="w-px h-4 bg-primary-200 dark:bg-primary-700" />
}

export const CommentApprovalMenu: React.FC<RichTextAreaCommentApprovalMenu> = ({
  onApproveComments,
}) => {
  const { editor } = useContext(RichTextAreaContext)
  if (!editor) {
    return null
  }
  return (
    <BaseCommentApprovalMenu
      editor={editor}
      onApproveComments={onApproveComments}
    />
  )
}

export const LinkMenu: React.FC = () => {
  const { editor } = useContext(RichTextAreaContext)
  if (!editor) {
    return null
  }
  return <BaseLinkMenu editor={editor} />
}

export const TextMenu: React.FC<RichTextAreaTextMenu> = ({ onAddComment }) => {
  const { editor } = useContext(RichTextAreaContext)
  if (!editor) {
    return null
  }
  return <BaseTextMenu editor={editor} onAddComment={onAddComment} />
}

export const Header: React.FC<RichTextAreaHeader> = ({
  className,
  buttonFocusClassName,
}) => {
  const { editor, isFocused } = useContext(RichTextAreaContext)
  return (
    <div
      className={twMerge(
        'flex items-center gap-x-1 p-1 w-full min-h-[34px] bg-primary-50 dark:bg-primary-800 rounded-t-md border-b',
        isFocused
          ? 'border-primary-300 dark:border-primary-700'
          : 'border-primary-200 dark:border-primary-700',
        className ?? '',
      )}
    >
      <div className="flex items-center gap-x-[2px]">
        <EditorButton
          isActive={editor?.isActive('bold') ?? false}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          buttonFocusClassName={buttonFocusClassName}
        >
          <TbBold className="w-4 h-4" />
          <EditorButton.Tooltip>
            <span>Bold</span>
          </EditorButton.Tooltip>
        </EditorButton>
        <EditorButton
          isActive={editor?.isActive('italic') ?? false}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          buttonFocusClassName={buttonFocusClassName}
        >
          <TbItalic className="w-4 h-4" />
          <EditorButton.Tooltip>
            <span>Italic</span>
          </EditorButton.Tooltip>
        </EditorButton>
        <EditorButton
          isActive={editor?.isActive('strike') ?? false}
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          buttonFocusClassName={buttonFocusClassName}
        >
          <TbStrikethrough className="w-4 h-4" />
          <EditorButton.Tooltip>
            <span>Strikethrough</span>
          </EditorButton.Tooltip>
        </EditorButton>
      </div>
      <HeaderDivider />
      <div className="flex items-center gap-x-[2px]">
        <EditorButton
          isActive={editor?.isActive('bulletList') ?? false}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          buttonFocusClassName={buttonFocusClassName}
        >
          <TbList className="w-4 h-4" />
          <EditorButton.Tooltip>
            <span>Bulleted List</span>
          </EditorButton.Tooltip>
        </EditorButton>
        <EditorButton
          isActive={editor?.isActive('orderedList') ?? false}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          buttonFocusClassName={buttonFocusClassName}
        >
          <TbListNumbers className="w-4 h-4" />
          <EditorButton.Tooltip>
            <span>Numbered List</span>
          </EditorButton.Tooltip>
        </EditorButton>
      </div>
    </div>
  )
}

const Footer = forwardRef<HTMLDivElement, RichTextAreaFooter>(
  ({ children, className }, ref) => {
    return (
      <div
        ref={ref}
        className={twMerge(
          'flex items-center justify-end w-full rounded-b-md cursor-text px-[6px] pb-[6px]',
          className ?? '',
        )}
      >
        {children}
      </div>
    )
  },
)

const RTABubbleMenu = forwardRef<HTMLDivElement, RichTextAreaBubbleMenu>(
  ({ className }, ref) => {
    const { editor } = useContext(RichTextAreaContext)

    if (!editor) {
      return null
    }

    return (
      <BubbleMenu editor={editor}>
        <div
          ref={ref}
          className={twMerge(
            'flex items-center rounded-md p-1 border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900',
            'divide-x divide-primary-200 dark:divide-primary-700',
            className ?? '',
          )}
        >
          <div className="flex items-center gap-x-1 pr-1">
            <EditorButton
              isActive={editor.isActive('bold')}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <TbBold className="w-4 h-4" />
              <EditorButton.Tooltip>
                <span>Bold</span>
              </EditorButton.Tooltip>
            </EditorButton>
            <EditorButton
              isActive={editor.isActive('italic')}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <TbItalic className="w-4 h-4" />
              <EditorButton.Tooltip>
                <span>Italic</span>
              </EditorButton.Tooltip>
            </EditorButton>
            <EditorButton
              isActive={editor.isActive('strike')}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <TbStrikethrough className="w-4 h-4" />
              <EditorButton.Tooltip>
                <span>Strikethrough</span>
              </EditorButton.Tooltip>
            </EditorButton>
          </div>
          <div className="flex items-center gap-x-1 pl-1">
            <EditorButton
              isActive={editor.isActive('bulletList')}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <TbList className="w-4 h-4" />
              <EditorButton.Tooltip>
                <span>Bulleted List</span>
              </EditorButton.Tooltip>
            </EditorButton>
            <EditorButton
              isActive={editor.isActive('orderedList')}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <TbListNumbers className="w-4 h-4" />
              <EditorButton.Tooltip>
                <span>Numbered List</span>
              </EditorButton.Tooltip>
            </EditorButton>
          </div>
        </div>
      </BubbleMenu>
    )
  },
)

const Content = forwardRef<HTMLDivElement, RichTextAreaContent>(
  ({ className, containerClassName, compact = false }, ref) => {
    const { editor } = useContext(RichTextAreaContext)
    return (
      <div
        className={twMerge(
          'px-2 py-2 cursor-text overflow-y-auto',
          containerClassName ?? '',
        )}
      >
        <EditorContent
          ref={ref}
          editor={editor}
          className={twMerge(
            'rich-text-editor-content my-0 max-w-full text-sm',
            PROSE_CLASSNAMES,
            compact
              ? 'prose-p:my-0 prose-headings:my-0 prose-ul:my-[6px] prose-ol:my-[6px] leading-[18px]'
              : 'prose-p:my-[18px] prose-headings:my-[18px] prose-ul:my-[10px] prose-ol:my-[10px] leading-[22px]',
            className ?? '',
          )}
          spellCheck={true}
        />
      </div>
    )
  },
)

interface RichTextAreaBaseProps extends RichTextAreaProps {
  editor: Editor | null
  doc: Y.Doc | null
  provider: TiptapCollabProvider | null
  isFocused: boolean
  containerRef: React.RefObject<HTMLDivElement>
  disabled?: boolean
  className?: string
  shiftEnter?: React.ReactNode
  filteredChildren: React.ReactNode
  handleClick: (event: React.MouseEvent<HTMLDivElement>) => void
  handleFocus: (event: React.FocusEvent<HTMLDivElement>) => void
  handleBlur: (event: React.FocusEvent<HTMLDivElement>) => void
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void
  setIsFocused: (isFocused: boolean) => void
}

const RichTextAreaBase = forwardRef<RichTextAreaRef, RichTextAreaBaseProps>(
  (
    {
      id,
      editor,
      doc,
      provider,
      isFocused,
      containerRef,
      disabled = false,
      className,
      shiftEnter,
      filteredChildren,
      collaborationId,
      handleClick,
      handleFocus,
      handleBlur,
      handleKeyDown,
      setIsFocused,
    },
    ref,
  ) => {
    const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
      handleClick?.(event)
      if (
        editor &&
        !(
          (event.target as HTMLElement).classList.contains(
            RICH_TEXT_AREA_OVERRIDE_FOCUS_CLASSNAME,
          ) ||
          (event.target as HTMLElement).closest(
            `.${RICH_TEXT_AREA_OVERRIDE_FOCUS_CLASSNAME}`,
          )
        )
      ) {
        editor.commands.focus()
      }
    }
    return (
      <RichTextAreaContextProvider
        editor={editor}
        doc={doc}
        provider={provider}
        isFocused={isFocused}
        collaborationId={collaborationId}
        setIsFocused={setIsFocused}
      >
        <div className="flex flex-col gap-y-1 w-full h-full">
          <div
            id={id}
            ref={containerRef}
            className={twMerge(
              'rich-text-area flex flex-col border rounded-md w-full h-full',
              'bg-white dark:bg-primary-800',
              'border-primary-200 dark:border-primary-700',
              'hover:border-primary-300 dark:hover:border-primary-600',
              'focus-within:border-primary-400 dark:focus-within:border-primary-600',
              'focus-within:hover:border-primary-400 dark:focus-within:hover:border-primary-600',
              'focus:outline-none',
              'transition-opacity duration-100',
              disabled && 'opacity-70 pointer-events-none',
              className ?? '',
            )}
            onClick={onClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            // TODO: figure out if this should be changed back to 0;
            // changed it to -1 because it was causing issues with the
            // focus behavior for the editor itself
            tabIndex={-1}
          >
            {filteredChildren}
          </div>
          {shiftEnter}
        </div>
      </RichTextAreaContextProvider>
    )
  },
)

const LocalRichTextArea = forwardRef<RichTextAreaRef, LocalRichTextAreaProps>(
  (props, ref) => {
    const [isFocused, setIsFocused] = useState<boolean>(false)

    const { editor, doc, ...methods } = useRichTextArea({
      ref,
      setIsFocused,
      ...props,
    })

    return (
      <RichTextAreaBase
        ref={ref}
        isFocused={isFocused}
        setIsFocused={setIsFocused}
        provider={null}
        doc={doc}
        editor={editor}
        {...props}
        {...methods}
      />
    )
  },
)

const CollaborativeRichTextArea = forwardRef<
  RichTextAreaRef,
  CollaborativeRichTextAreaProps
>(({ value, ...props }, ref) => {
  const [isFocused, setIsFocused] = useState<boolean>(false)

  const { isSynced, ...methods } = useCollaborativeRichTextArea({
    ref,
    setIsFocused,
    value,
    ...props,
  })

  if (!isSynced) {
    return (
      // if the editor is collaborative but the content is not synced,
      // we want to show the local editor with a "loading" indicator
      <div className="h-full w-full opacity-50 pointer-events-none">
        <LocalRichTextArea ref={ref} {...props} value={value} disabled={true} />
      </div>
    )
  }

  return (
    <RichTextAreaBase
      ref={ref}
      isFocused={isFocused}
      setIsFocused={setIsFocused}
      {...props}
      {...methods}
    />
  )
})

const RichTextAreaWrapper = forwardRef<RichTextAreaRef, RichTextAreaProps>(
  ({ valueType = 'markdown', ...props }, ref) => {
    const { collaborationId } = props
    const { activeOrgId } = useActiveOrgId()
    const { data: tiptapToken, isLoading } = useTiptapToken(
      activeOrgId,
      collaborationId ?? '',
    )

    if (
      collaborationId === undefined ||
      tiptapToken === undefined ||
      isLoading
    ) {
      return (
        <div
          className={twMerge(
            'h-full w-full',
            // if this editor is collaborative but the token is not loaded,
            // we want to show the local editor with whatever the most recent content was;
            // this is because we should NOT instantiate the collaborative
            // editor until we have the token so the tiptap editor loads with
            // the correct extensions
            collaborationId !== undefined
              ? 'opacity-50 pointer-events-none'
              : '',
          )}
        >
          <LocalRichTextArea
            ref={ref}
            valueType={valueType}
            // disable the editor if it is disabled or if it is collaborative
            // and the content is not yet synced
            disabled={props.disabled || collaborationId !== undefined}
            {...props}
          />
        </div>
      )
    }

    return (
      <CollaborativeRichTextArea
        ref={ref}
        valueType={valueType}
        collaborationConfig={{
          documentId: collaborationId,
          token: tiptapToken,
        }}
        {...props}
      />
    )
  },
)

const RichTextAreaStoreWrapper = forwardRef<RichTextAreaRef, RichTextAreaProps>(
  (props, ref) => {
    return (
      <RichTextAreaStoreContextProvider>
        <RichTextAreaWrapper ref={ref} {...props} />
      </RichTextAreaStoreContextProvider>
    )
  },
)

RTABubbleMenu.displayName = 'RichTextArea.BubbleMenu'
TextMenu.displayName = 'RichTextArea.TextMenu'
LinkMenu.displayName = 'RichTextArea.LinkMenu'
Content.displayName = 'RichTextArea.Content'
Header.displayName = 'RichTextArea.Header'
Footer.displayName = 'RichTextArea.Footer'
EditorToolbar.displayName = 'RichTextArea.Toolbar'
VersionDialog.displayName = 'RichTextArea.VersionDialog'
LocalRichTextArea.displayName = 'RichTextArea.Local'
CollaborativeRichTextArea.displayName = 'RichTextArea.Collaborative'
RichTextAreaBase.displayName = 'RichTextArea.Base'
RichTextAreaWrapper.displayName = 'RichTextArea.Wrapper'
RichTextAreaStoreWrapper.displayName = 'RichTextArea'

const RichTextArea = Object.assign(RichTextAreaStoreWrapper, {
  AttachmentMenu,
  Attachments,
  BubbleMenu: RTABubbleMenu,
  CommentApprovalMenu,
  CancelButton,
  Content,
  Header,
  Footer,
  LinkMenu,
  SaveButton,
  ShiftEnterMessage,
  SubmitButton,
  TextMenu,
  Toolbar: EditorToolbar,
  VersionDialog,
})

export {
  RICH_TEXT_AREA_NO_BLUR_CLASSNAME,
  RICH_TEXT_AREA_OVERRIDE_FOCUS_CLASSNAME,
  convertToJsonContent,
  getHtmlFromJsonContent,
  getPlainTextFromJsonContent,
  isValidJsonContent,
  renderAsComponent,
  RichTextArea,
  useRichTextAreaContext,
  type RichTextAreaRef,
}
